import { ChevronDown, Plus, RefreshCw, Search, SlidersHorizontal, Trash, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

/**
 * The sessions toolbar, ported from the designer's `.sc-sessions-toolbar`.
 *
 * Left: bulk Delete, Search, a Filter menu and the chosen filters as removable chips.
 * Right: Refresh and Start Session — both kept. The design has no Start Session
 * button because it opens its drawer from a window event fired elsewhere; removing
 * ours would leave no way to create a session at all.
 */
export function SessionsToolbar({
  search,
  onSearchChange,
  filters,
  filterOptions,
  onAddFilter,
  onRemoveFilter,
  onClearFilters,
  selectedCount,
  onDeleteSelected,
  onRefresh,
  isRefreshing,
  onStartSession,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  /** Currently applied filter keys. */
  filters: string[];
  /** Every filter the list offers, as `{ key, label }`. */
  filterOptions: Array<{ key: string; label: string }>;
  onAddFilter: (key: string) => void;
  onRemoveFilter: (key: string) => void;
  onClearFilters: () => void;
  selectedCount: number;
  onDeleteSelected: () => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  onStartSession: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close on any outside click. Scoped to the wrapper so clicking the button itself
  // still toggles rather than being closed and reopened in the same tick.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [menuOpen]);

  const labelFor = (key: string) => filterOptions.find((o) => o.key === key)?.label ?? key;

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-3.5">
        {/* Bulk delete. Disabled rather than hidden with nothing selected, so the
            column of checkboxes has a visible purpose before you use it. */}
        <button
          type="button"
          onClick={onDeleteSelected}
          disabled={selectedCount === 0}
          className="inline-flex cursor-pointer items-center gap-1.5 border-none bg-transparent text-[length:var(--text-14)] font-medium text-[#FF383C] transition-opacity hover:opacity-80 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Trash size={16} className="shrink-0" />
          <span>Delete{selectedCount > 0 ? ` (${selectedCount})` : ''}</span>
        </button>

        <div className="inline-flex w-[clamp(180px,15vw,230px)] items-center gap-2 rounded-lg bg-white px-3 py-1.5 ring-1 ring-[#D0D4E4] transition-all focus-within:ring-[#0048FF]">
          <Search size={16} className="shrink-0 text-[#9CA3AF]" />
          <input
            type="text"
            placeholder="Search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search sessions"
            className="w-full border-none bg-transparent text-[length:var(--text-14)] leading-normal text-[#041A3A] outline-none placeholder:text-[#9CA3AF]"
          />
        </div>

        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border-none bg-white px-3 py-1.5 text-[length:var(--text-14)] font-medium text-[#4B5563] ring-1 ring-[#D0D4E4] transition-colors hover:bg-gray-50"
          >
            <SlidersHorizontal size={14} className="shrink-0 text-[#6B7280]" />
            <span>Filter</span>
            <ChevronDown size={14} className="shrink-0 text-[#6B7280]" />
          </button>

          {menuOpen && (
            <div
              role="menu"
              className="absolute left-0 top-10 z-[120] flex w-52 flex-col rounded-xl border border-[#E5E7EB] bg-white py-1.5 shadow-[0_4px_20px_rgba(0,0,0,0.12)]"
            >
              {filterOptions.map((option) => {
                const active = filters.includes(option.key);
                return (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      // Toggling from the menu, not just adding: re-picking an active
                      // filter is the obvious way to undo it without hunting for the
                      // chip's ✕.
                      if (active) onRemoveFilter(option.key);
                      else onAddFilter(option.key);
                      setMenuOpen(false);
                    }}
                    className={cn(
                      'w-full cursor-pointer border-none bg-transparent px-3.5 py-2 text-left text-[length:var(--text-13)] transition-colors hover:bg-gray-50',
                      active ? 'bg-blue-50/50 font-semibold text-[#0048FF]' : 'text-gray-700',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {filters.map((key) => (
            <span
              key={key}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#F4F6FD] px-2.5 py-1.5 text-[length:var(--text-12)] font-medium leading-none text-[#336DFF]"
            >
              {labelFor(key)}
              <button
                type="button"
                onClick={() => onRemoveFilter(key)}
                aria-label={`Remove ${labelFor(key)} filter`}
                className="inline-flex cursor-pointer items-center justify-center border-none bg-transparent p-0.5 leading-none text-[#0048FF] hover:opacity-75"
              >
                <X size={12} />
              </button>
            </span>
          ))}

          {filters.length > 0 && (
            <button
              type="button"
              onClick={onClearFilters}
              className="ml-1 cursor-pointer border-none bg-transparent text-[length:var(--text-12)] font-medium leading-none text-[#666666] hover:underline"
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      <div className="ms-auto flex shrink-0 items-center gap-3">
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg border border-[#B3B3B3] bg-white px-[clamp(14px,1.4vw,18px)] py-[clamp(5px,0.4vw,7px)] text-[length:var(--text-14)] font-medium text-[#666666] hover:bg-gray-50"
        >
          <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : undefined} /> Refresh
        </button>
        <button
          type="button"
          onClick={onStartSession}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-[#0048FF] px-[clamp(16px,2vw,25px)] py-[clamp(5px,0.4vw,7px)] text-[length:var(--text-14)] font-medium text-white hover:bg-[#0a52e0]"
        >
          <Plus size={14} /> Start Session
        </button>
      </div>
    </div>
  );
}
